import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../api/axiosInstance';

const initialState = {
  canTransition: false,
  transitionData: null,
  openItems: null,
  yearsWithOpenItems: [],
  selectedYearId: null,
  loading: false,
  error: null
};

export const checkCanTransition = createAsyncThunk(
  'yearTransition/checkCanTransition',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/api/settings/school-years/can-transition/');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const startTransition = createAsyncThunk(
  'yearTransition/startTransition',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/settings/school-years/start-transition/');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchYearsWithOpenItems = createAsyncThunk(
  'yearTransition/fetchYearsWithOpenItems',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/api/settings/school-years/years-with-open-items/');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchOpenItems = createAsyncThunk(
  'yearTransition/fetchOpenItems',
  async (yearId, { rejectWithValue }) => {
    try {
      const url = yearId 
        ? `/api/settings/school-years/open-items/?year_id=${yearId}`
        : '/api/settings/school-years/open-items/';
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const closeStudentFee = createAsyncThunk(
  'yearTransition/closeStudentFee',
  async ({ studentId, yearId, amount }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/settings/school-years/close-student-fee/', {
        student_id: studentId,
        year_id: yearId,
        amount,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const closeAllStudentFees = createAsyncThunk(
  'yearTransition/closeAllStudentFees',
  async ({ yearId }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/settings/school-years/close-all-student-fees/', {
        year_id: yearId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const transferUncollectedCheques = createAsyncThunk(
  'yearTransition/transferUncollectedCheques',
  async ({ yearId, ids }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/payments/recipients/transfer-cheques-to-next-year/', {
        year_id: yearId,
        ...(ids ? { ids } : {}),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const transferPendingPayments = createAsyncThunk(
  'yearTransition/transferPendingPayments',
  async ({ yearId, ids }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/payments/payments/transfer-cheques-to-next-year/', {
        year_id: yearId,
        ...(ids ? { ids } : {}),
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchPendingTransferCheques = createAsyncThunk(
  'yearTransition/fetchPendingTransferCheques',
  async ({ yearId, search }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ year_id: yearId });
      if (search) params.append('search', search);
      const response = await axios.get(`/api/payments/recipients/pending-transfer/?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const undoTransferCheques = createAsyncThunk(
  'yearTransition/undoTransferCheques',
  async ({ ids }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/payments/recipients/undo-transfer/', { ids });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchPendingTransferPayments = createAsyncThunk(
  'yearTransition/fetchPendingTransferPayments',
  async ({ yearId, search }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ year_id: yearId });
      if (search) params.append('search', search);
      const response = await axios.get(`/api/payments/payments/pending-transfer/?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const undoTransferPayments = createAsyncThunk(
  'yearTransition/undoTransferPayments',
  async ({ ids }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/payments/payments/undo-transfer/', { ids });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const fetchTransferredStudents = createAsyncThunk(
  'yearTransition/fetchTransferredStudents',
  async ({ yearId, search }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({ year_id: yearId });
      if (search) params.append('search', search);
      const response = await axios.get(`/api/settings/school-years/transferred-students/?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const reopenStudentFee = createAsyncThunk(
  'yearTransition/reopenStudentFee',
  async ({ studentId, yearId }, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/settings/school-years/reopen-student-fee/', {
        student_id: studentId,
        year_id: yearId,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const completeTransition = createAsyncThunk(
  'yearTransition/completeTransition',
  async (transitionId, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/settings/school-years/complete-transition/', {
        transition_id: transitionId
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

const yearTransitionSlice = createSlice({
  name: 'yearTransition',
  initialState,
  reducers: {
    clearTransition: (state) => {
      state.transitionData = null;
      state.openItems = null;
    },
    setSelectedYear: (state, action) => {
      state.selectedYearId = action.payload;
    },
    setTransitionData: (state, action) => {
      state.transitionData = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkCanTransition.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkCanTransition.fulfilled, (state, action) => {
        state.loading = false;
        state.canTransition = action.payload.can_transition;
        // Restore an already-started transition (e.g. after a page reload) so the
        // "إتمام الانتقال" button reappears instead of being lost with the page state.
        if (action.payload.has_active_transition && action.payload.transition_id) {
          state.transitionData = {
            transition_id: action.payload.transition_id,
            new_year: action.payload.new_year || null,
          };
        }
      })
      .addCase(checkCanTransition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(startTransition.pending, (state) => {
        state.loading = true;
      })
      .addCase(startTransition.fulfilled, (state, action) => {
        state.loading = false;
        state.transitionData = action.payload;
      })
      .addCase(startTransition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchOpenItems.pending, (state) => {
        state.loading = true;
        // Clear old data when fetching new year's data to prevent showing wrong year's data
        state.openItems = null;
      })
      .addCase(fetchOpenItems.fulfilled, (state, action) => {
        state.loading = false;
        state.openItems = action.payload;
      })
      .addCase(fetchOpenItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.openItems = null; // Clear on error too
      })
      .addCase(fetchYearsWithOpenItems.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchYearsWithOpenItems.fulfilled, (state, action) => {
        state.loading = false;
        state.yearsWithOpenItems = action.payload.years || [];
      })
      .addCase(fetchYearsWithOpenItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(closeStudentFee.pending, (state) => {
        state.loading = true;
      })
      .addCase(closeStudentFee.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(closeStudentFee.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(closeAllStudentFees.pending, (state) => {
        state.loading = true;
      })
      .addCase(closeAllStudentFees.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(closeAllStudentFees.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(transferUncollectedCheques.pending, (state) => {
        state.loading = true;
      })
      .addCase(transferUncollectedCheques.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(transferUncollectedCheques.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(transferPendingPayments.pending, (state) => {
        state.loading = true;
      })
      .addCase(transferPendingPayments.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(transferPendingPayments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(completeTransition.pending, (state) => {
        state.loading = true;
      })
      .addCase(completeTransition.fulfilled, (state) => {
        state.loading = false;
        state.transitionData = null;
        state.openItems = null;
      })
      .addCase(completeTransition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearTransition, setSelectedYear, setTransitionData } = yearTransitionSlice.actions;
export default yearTransitionSlice.reducer;
